import { useMutation, useQueryClient } from '@tanstack/react-query'

import config from '@config'

import {
  SportEntitySubscription,
  SportEntitySubscriptionsAPIResponse,
} from './useSportEntitySubscriptions'

const {
  nemosyne: { baseUrl, setEntitySubscriptionUrl },
} = config

export type SubscribeToEntityType = SportEntitySubscription

const subscribeToEntity = async ({
  entityId,
  entityType,
  channel,
}: SubscribeToEntityType) => {
  const bodyPayload = {
    entityId,
    entityType,
    channel,
  }

  const response = await fetch(`${baseUrl}${setEntitySubscriptionUrl}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(bodyPayload),
  })

  if (!response.ok) {
    throw new Error(`Server responded with ${response.status}`)
  }

  return response.json() as Promise<SportEntitySubscription>
}

const useSportEntitySubscriptionsSubscribe = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: subscribeToEntity,
    onMutate: async (newEntitySubscriptionData) => {
      const { entityType } = newEntitySubscriptionData

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ['sport-entity-subscriptions', entityType],
      })

      // Snapshot the previous value
      const previousEntitySubscriptionData = queryClient.getQueryData<
        SportEntitySubscriptionsAPIResponse | undefined
      >(['sport-entity-subscriptions', entityType])

      // Optimistically update to the new value
      queryClient.setQueryData<SportEntitySubscriptionsAPIResponse | undefined>(
        ['sport-entity-subscriptions', entityType],
        (oldData) =>
          [...(oldData ?? []), newEntitySubscriptionData].sort(
            (a, b) => Number(a.entityId) - Number(b.entityId)
          )
      )

      // Return a context object with the snapshotted value
      return { previousEntitySubscriptionData }
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (error, newEntitySubscriptionData, context) => {
      queryClient.setQueryData(
        ['sport-entity-subscriptions', newEntitySubscriptionData.entityType],
        context?.previousEntitySubscriptionData
      )
      console.error('An error happened while subscribing to the entity:', error)
    },
    // Always refetch after error or success:
    onSettled: (_data, _error, newEntitySubscriptionData) =>
      queryClient.invalidateQueries({
        queryKey: [
          'sport-entity-subscriptions',
          newEntitySubscriptionData.entityType,
        ],
      }),
  })
}

export default useSportEntitySubscriptionsSubscribe
